<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  CUSTOM_ROLE_PERMISSION_GROUPS,
  CUSTOM_ROLE_PRESETS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const modalTitle = computed(() =>
  props.mode === 'edit'
    ? t('CUSTOM_ROLE.EDIT.TITLE')
    : t('CUSTOM_ROLE.ADD.TITLE')
);
const modalDescription = computed(() =>
  props.mode === 'edit' ? t('CUSTOM_ROLE.EDIT.DESC') : t('CUSTOM_ROLE.ADD.DESC')
);
const submitButtonText = computed(() =>
  props.mode === 'edit'
    ? t('CUSTOM_ROLE.EDIT.SUBMIT')
    : t('CUSTOM_ROLE.ADD.SUBMIT')
);

const presetLabels = computed(() => ({
  OPERATOR: t('CUSTOM_ROLE.PRESETS.OPERATOR'),
  CORRETOR: t('CUSTOM_ROLE.PRESETS.CORRETOR'),
  ADMIN: t('CUSTOM_ROLE.PRESETS.ADMIN'),
}));

const groupLabels = computed(() => ({
  CONVERSATIONS: t('CUSTOM_ROLE.GROUPS.CONVERSATIONS'),
  CORE: t('CUSTOM_ROLE.GROUPS.CORE'),
  REAL_ESTATE: t('CUSTOM_ROLE.GROUPS.REAL_ESTATE'),
  APOIO: t('CUSTOM_ROLE.GROUPS.APOIO'),
  DATA_REGISTRY: t('CUSTOM_ROLE.GROUPS.DATA_REGISTRY'),
  POST_SALE: t('CUSTOM_ROLE.GROUPS.POST_SALE'),
  SALES: t('CUSTOM_ROLE.GROUPS.SALES'),
}));

const permissionLabels = computed(() => ({
  conversation_manage: t('CUSTOM_ROLE.PERMISSIONS.CONVERSATION_MANAGE'),
  conversation_unassigned_manage: t(
    'CUSTOM_ROLE.PERMISSIONS.CONVERSATION_UNASSIGNED_MANAGE'
  ),
  conversation_participating_manage: t(
    'CUSTOM_ROLE.PERMISSIONS.CONVERSATION_PARTICIPATING_MANAGE'
  ),
  contact_manage: t('CUSTOM_ROLE.PERMISSIONS.CONTACT_MANAGE'),
  report_manage: t('CUSTOM_ROLE.PERMISSIONS.REPORT_MANAGE'),
  support_manage: t('CUSTOM_ROLE.PERMISSIONS.SUPPORT_MANAGE'),
  knowledge_base_manage: t('CUSTOM_ROLE.PERMISSIONS.KNOWLEDGE_BASE_MANAGE'),
  imoveis_manage: t('CUSTOM_ROLE.PERMISSIONS.IMOVEIS_MANAGE'),
  visitas_manage: t('CUSTOM_ROLE.PERMISSIONS.VISITAS_MANAGE'),
  captacao_manage: t('CUSTOM_ROLE.PERMISSIONS.CAPTACAO_MANAGE'),
  financeiro_view: t('CUSTOM_ROLE.PERMISSIONS.FINANCEIRO_VIEW'),
  financeiro_manage: t('CUSTOM_ROLE.PERMISSIONS.FINANCEIRO_MANAGE'),
  apoio_manage: t('CUSTOM_ROLE.PERMISSIONS.APOIO_MANAGE'),
  vitrine_manage: t('CUSTOM_ROLE.PERMISSIONS.VITRINE_MANAGE'),
  leads_manage: t('CUSTOM_ROLE.PERMISSIONS.LEADS_MANAGE'),
  leads_send_crm: t('CUSTOM_ROLE.PERMISSIONS.LEADS_SEND_CRM'),
  chaves_manage: t('CUSTOM_ROLE.PERMISSIONS.CHAVES_MANAGE'),
  agency_manage: t('CUSTOM_ROLE.PERMISSIONS.AGENCY_MANAGE'),
  partners_manage: t('CUSTOM_ROLE.PERMISSIONS.PARTNERS_MANAGE'),
  manutencao_view: t('CUSTOM_ROLE.PERMISSIONS.MANUTENCAO_VIEW'),
  manutencao_manage: t('CUSTOM_ROLE.PERMISSIONS.MANUTENCAO_MANAGE'),
  data_registry_view: t('CUSTOM_ROLE.PERMISSIONS.DATA_REGISTRY_VIEW'),
  data_registry_manage: t('CUSTOM_ROLE.PERMISSIONS.DATA_REGISTRY_MANAGE'),
  data_registry_sensitive_view: t(
    'CUSTOM_ROLE.PERMISSIONS.DATA_REGISTRY_SENSITIVE_VIEW'
  ),
  post_sale_view: t('CUSTOM_ROLE.PERMISSIONS.POST_SALE_VIEW'),
  post_sale_manage: t('CUSTOM_ROLE.PERMISSIONS.POST_SALE_MANAGE'),
  post_sale_assign: t('CUSTOM_ROLE.PERMISSIONS.POST_SALE_ASSIGN'),
  post_sale_status_change: t('CUSTOM_ROLE.PERMISSIONS.POST_SALE_STATUS_CHANGE'),
  transactions_view: t('CUSTOM_ROLE.PERMISSIONS.TRANSACTIONS_VIEW'),
  transactions_manage: t('CUSTOM_ROLE.PERMISSIONS.TRANSACTIONS_MANAGE'),
}));

const applyPreset = preset => {
  selectedPermissions.value = [...new Set(preset.permissions)];
  v$.value.selectedPermissions.$touch();
};

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }">
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </label>
        <div class="mt-2 flex flex-wrap gap-2">
          <Button
            v-for="preset in CUSTOM_ROLE_PRESETS"
            :key="preset.key"
            type="button"
            faded
            slate
            size="sm"
            :label="presetLabels[preset.key]"
            @click.prevent="applyPreset(preset)"
          />
        </div>
        <div class="mb-4 mt-4 flex flex-col gap-4">
          <div
            v-for="group in CUSTOM_ROLE_PERMISSION_GROUPS"
            :key="group.key"
            class="flex flex-col gap-2"
          >
            <span class="text-xs font-medium text-n-slate-11">
              {{ groupLabels[group.key] }}
            </span>
            <label
              v-for="permission in group.permissions"
              :key="permission"
              class="flex items-center text-sm font-normal"
            >
              <input
                :id="permission"
                v-model="selectedPermissions"
                type="checkbox"
                :value="permission"
                name="permissions"
                class="ltr:mr-2 rtl:ml-2"
              />
              <span>
                {{ permissionLabels[permission] }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
